import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { User, BarChart3, LogOut, Calculator, Trophy, Flame } from "lucide-react";
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { useUnifiedStats } from "@/hooks/useUnifiedStats";
import { useAchievements } from "@/hooks/useAchievements";
import { Badge } from "@/components/ui/badge";

interface UserMenuProps {
  onStatsClick?: () => void;
}

export function UserMenu({ onStatsClick }: UserMenuProps) {
  const { user, profile, signOut } = useAuth();
  const stats = useUnifiedStats();
  const { achievements, earnedAchievements } = useAchievements();
  const navigate = useNavigate();
  const [isRecalculating, setIsRecalculating] = useState(false);

  // Get latest earned achievement
  const latestAchievement = earnedAchievements.length > 0
    ? achievements.find(a => a.id === earnedAchievements[earnedAchievements.length - 1].achievement_id)
    : null;

  const getInitials = () => {
    if (profile?.display_name) {
      return profile.display_name.charAt(0).toUpperCase();
    }
    return user?.email?.charAt(0).toUpperCase() || "?";
  };

  const getDisplayName = () => {
    return profile?.display_name || user?.email?.split('@')[0] || "User";
  };

  const handleSignOut = async () => {
    await signOut();
    navigate("/play");
  };

  const handleRecalculateStats = async () => {
    if (!user) return;
    
    setIsRecalculating(true);
    try {
      const { error } = await supabase.rpc("recalculate_user_stats", {
        p_user_id: user.id
      });
      
      if (error) throw error;
      
      // Clear the v2 flag to allow auto-recalc to run again if needed
      localStorage.removeItem("wormble-stats-recalculated-v2");
      
      // Refresh stats
      await stats.refreshStats();
      
      toast({
        title: "Stats recalculated!",
        description: "Your statistics have been updated from your game history.",
      });
    } catch (error) {
      console.error("Error recalculating stats:", error);
      toast({
        title: "Recalculation failed",
        description: "Could not recalculate your stats. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRecalculating(false);
    }
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="h-11 w-11 sm:h-9 sm:w-9 rounded-full bg-card/40 hover:bg-card/60 backdrop-blur-sm"
          aria-label="User menu"
          data-user-menu
        >
          <Avatar className="h-7 w-7 sm:h-6 sm:w-6">
            <AvatarFallback className="bg-primary/10 text-primary text-sm">
              {getInitials()}
            </AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-72 bg-popover">
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <p className="text-sm font-medium leading-none">{getDisplayName()}</p>
            <p className="text-xs leading-none text-muted-foreground">
              {user?.email}
            </p>
          </div>
        </DropdownMenuLabel>

        {/* Quick Stats Card */}
        <div className="mx-2 my-2 rounded-lg bg-muted/50 p-3">
          <div className="grid grid-cols-3 gap-3 text-center">
            <div>
              <div className="text-lg font-bold text-foreground">{stats.totalPlayed}</div>
              <div className="text-xs text-muted-foreground">Played</div>
            </div>
            <div>
              <div className="text-lg font-bold text-foreground">{stats.winPercentage}%</div>
              <div className="text-xs text-muted-foreground">Win Rate</div>
            </div>
            <div>
              <div className="flex items-center justify-center gap-1">
                <Flame className="h-4 w-4 text-orange-500" />
                <span className="text-lg font-bold text-foreground">{stats.currentStreak}</span>
              </div>
              <div className="text-xs text-muted-foreground">Streak</div>
            </div>
          </div>
        </div>

        {/* Latest Achievement Preview */}
        {latestAchievement && (
          <button
            onClick={() => navigate('/profile#achievements')}
            className="mx-2 my-2 flex w-[calc(100%-1rem)] items-center gap-3 rounded-lg bg-muted/30 p-3 transition-colors hover:bg-muted/50"
          >
            <div className="text-2xl">{latestAchievement.icon}</div>
            <div className="flex-1 text-left">
              <div className="text-sm font-medium text-foreground">{latestAchievement.name}</div>
              <div className="text-xs text-muted-foreground">Latest Achievement</div>
            </div>
            <Badge variant="secondary" className="text-xs">
              {latestAchievement.tier}
            </Badge>
          </button>
        )}

        <DropdownMenuSeparator />
        {onStatsClick && (
          <DropdownMenuItem onClick={onStatsClick} className="cursor-pointer">
            <BarChart3 className="mr-2 h-4 w-4" />
            <span>Statistics</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
          <User className="mr-2 h-4 w-4" />
          <span>View Full Profile</span>
        </DropdownMenuItem>
        <DropdownMenuItem 
          onClick={handleRecalculateStats} 
          disabled={isRecalculating}
          className="cursor-pointer"
        >
          <Calculator className={`mr-2 h-4 w-4 ${isRecalculating ? 'animate-spin' : ''}`} />
          <span>{isRecalculating ? "Recalculating..." : "Recalculate Stats"}</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={handleSignOut} className="cursor-pointer text-destructive focus:text-destructive">
          <LogOut className="mr-2 h-4 w-4" />
          <span>Sign Out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
